import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { ConversationMessage } from '../types/conversation.js';
import type {
  Conversation,
  BreakdownLevel,
  BreakdownHierarchyNode,
} from './breakdown-api.js';
import * as breakdownApi from './breakdown-api.js';
import { wsClient } from './websocket-client.js';

export type BreakdownLoadingState = 'idle' | 'loading' | 'loaded' | 'error';
export type SendState = 'idle' | 'sending';

// ─── Shared State (module-level singleton) ──────────────────────────

let sharedConversation: Conversation | null = null;
let sharedMessages: ConversationMessage[] = [];
let sharedHierarchy: BreakdownHierarchyNode[] = [];

let sharedLoadingState: BreakdownLoadingState = 'idle';
let sharedHierarchyLoadingState: BreakdownLoadingState = 'idle';
let sharedSendState: SendState = 'idle';
let sharedError: string | null = null;

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

// ─── Shared Load Functions ──────────────────────────────────────────

async function loadConversation(id: number): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    const result = await breakdownApi.getConversation(id);
    sharedConversation = result.conversation;
    sharedMessages = result.messages;
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Konversation konnte nicht geladen werden';
  }

  notifyHosts();
}

async function loadConversationByPeriod(
  type: BreakdownLevel,
  period: string,
): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    const result = await breakdownApi.getConversationByPeriod(type, period);
    sharedConversation = result.conversation;
    sharedMessages = result.messages;
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    if (e instanceof Error && e.message.includes('404')) {
      sharedError = null;
      sharedLoadingState = 'loaded';
      sharedConversation = null;
      sharedMessages = [];
    } else {
      sharedError = e instanceof Error ? e.message : 'Konversation konnte nicht geladen werden';
    }
  }

  notifyHosts();
}

async function loadHierarchy(): Promise<void> {
  if (sharedHierarchyLoadingState === 'loading') return;
  sharedHierarchyLoadingState = 'loading';
  notifyHosts();

  try {
    sharedHierarchy = await breakdownApi.getHierarchy();
    sharedHierarchyLoadingState = 'loaded';
  } catch (e) {
    sharedHierarchyLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Hierarchie konnte nicht geladen werden';
  }

  notifyHosts();
}

// ─── WebSocket Listener (module-level) ──────────────────────────────

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as {
    type: string;
    payload?: { conversationId?: number };
  };

  if (
    detail.type === 'conversation:message-received' &&
    sharedConversation &&
    detail.payload?.conversationId === sharedConversation.id
  ) {
    void loadConversation(sharedConversation.id);
  }

  if (detail.type === 'breakdown:goals-confirmed') {
    void loadHierarchy();
    if (
      sharedConversation &&
      detail.payload?.conversationId === sharedConversation.id
    ) {
      void loadConversation(sharedConversation.id);
    }
  }

  if (detail.type === 'goal:updated') {
    void loadHierarchy();
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

// ─── Reactive Controller Class ──────────────────────────────────────

export class BreakdownStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  // ─── Getters ────────────────────────────────────────────────────

  get conversation(): Conversation | null {
    return sharedConversation;
  }

  get messages(): ConversationMessage[] {
    return sharedMessages;
  }

  get hierarchy(): BreakdownHierarchyNode[] {
    return sharedHierarchy;
  }

  get loadingState(): BreakdownLoadingState {
    return sharedLoadingState;
  }

  get hierarchyLoadingState(): BreakdownLoadingState {
    return sharedHierarchyLoadingState;
  }

  get sendState(): SendState {
    return sharedSendState;
  }

  get error(): string | null {
    return sharedError;
  }

  get hasConversation(): boolean {
    return sharedConversation !== null;
  }

  // ─── Public Methods ─────────────────────────────────────────────

  async loadConversation(id: number): Promise<void> {
    await loadConversation(id);
  }

  async loadByPeriod(type: BreakdownLevel, period: string): Promise<void> {
    await loadConversationByPeriod(type, period);
  }

  async loadHierarchy(): Promise<void> {
    await loadHierarchy();
  }

  async startConversation(type: BreakdownLevel, period: string): Promise<void> {
    if (sharedSendState === 'sending') return;
    sharedSendState = 'sending';
    sharedError = null;
    notifyHosts();

    try {
      const result = await breakdownApi.startConversation(type, period);
      sharedConversation = result.conversation;
      sharedMessages = result.firstMessage ? [result.firstMessage] : [];
      sharedLoadingState = 'loaded';
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Konversation konnte nicht gestartet werden';
    }

    sharedSendState = 'idle';
    notifyHosts();
  }

  async sendMessage(content: string): Promise<void> {
    if (!sharedConversation || sharedSendState === 'sending') return;
    sharedSendState = 'sending';
    sharedError = null;

    // Optimistic update: add user message immediately
    const optimisticMsg: ConversationMessage = {
      id: -Date.now(),
      conversation_id: sharedConversation.id,
      role: 'user',
      content,
      metadata: null,
      created_at: new Date().toISOString(),
    };
    sharedMessages = [...sharedMessages, optimisticMsg];
    notifyHosts();

    try {
      const result = await breakdownApi.sendMessage(sharedConversation.id, content);
      // Replace optimistic message with real one and add assistant response
      sharedMessages = [
        ...sharedMessages.filter((m) => m.id !== optimisticMsg.id),
        result.userMessage,
        result.assistantMessage,
      ];
    } catch (e) {
      // Remove optimistic message on error
      sharedMessages = sharedMessages.filter((m) => m.id !== optimisticMsg.id);
      sharedError = e instanceof Error ? e.message : 'Nachricht konnte nicht gesendet werden';
    }

    sharedSendState = 'idle';
    notifyHosts();
  }

  async confirmBreakdowns(): Promise<void> {
    if (!sharedConversation || sharedSendState === 'sending') return;
    sharedSendState = 'sending';
    sharedError = null;
    notifyHosts();

    try {
      const result = await breakdownApi.confirmBreakdowns(sharedConversation.id);
      sharedConversation = result.conversation;
      // Reload hierarchy to reflect new breakdowns
      void loadHierarchy();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Breakdowns konnten nicht bestaetigt werden';
    }

    sharedSendState = 'idle';
    notifyHosts();
  }

  async updateGoalStatus(
    goalId: number,
    status: breakdownApi.BreakdownStatus,
  ): Promise<void> {
    try {
      await breakdownApi.updateGoalStatus(goalId, status);
      void loadHierarchy();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Status konnte nicht aktualisiert werden';
      notifyHosts();
    }
  }

  async updateGoal(
    goalId: number,
    updates: { title?: string; description?: string; priority?: string },
  ): Promise<void> {
    try {
      await breakdownApi.updateGoal(goalId, updates);
      void loadHierarchy();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Ziel konnte nicht aktualisiert werden';
      notifyHosts();
    }
  }

  async deleteAllGoals(): Promise<boolean> {
    try {
      await breakdownApi.deleteAllGoals();
      void loadHierarchy();
      return true;
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Ziele konnten nicht geloescht werden';
      notifyHosts();
      return false;
    }
  }

  clearError(): void {
    sharedError = null;
    notifyHosts();
  }

  reset(): void {
    sharedConversation = null;
    sharedMessages = [];
    sharedLoadingState = 'idle';
    sharedSendState = 'idle';
    sharedError = null;
    notifyHosts();
  }
}
